import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  MessageSquare, Users, Plus, Trash2, Send, Loader2,
  CheckCircle, AlertCircle, Phone, Clock, ChevronDown, ChevronUp
} from 'lucide-react';

const API = '/api';

export default function WhatsAppBulk() {
  const [status, setStatus] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // New contact form
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [addingContact, setAddingContact] = useState(false);

  // Bulk send form
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedContacts, setSelectedContacts] = useState({});
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statusRes, contactsRes, templatesRes, jobsRes] = await Promise.all([
        axios.get(`${API}/social/whatsapp/status`, { withCredentials: true }),
        axios.get(`${API}/social/whatsapp/contacts`, { withCredentials: true }),
        axios.get(`${API}/social/whatsapp/templates`, { withCredentials: true }).catch(() => ({ data: { templates: [] } })),
        axios.get(`${API}/social/whatsapp/jobs`, { withCredentials: true }),
      ]);
      setStatus(statusRes.data);
      setContacts(contactsRes.data.contacts || []);
      setTemplates(templatesRes.data.templates || []);
      setJobs(jobsRes.data.jobs || []);
    } catch (e) {
      toast.error('Failed to load WhatsApp data');
    } finally {
      setLoading(false);
    }
  };

  const addContact = async () => {
    if (!newPhone.trim()) { toast.error('Phone number required'); return; }
    setAddingContact(true);
    try {
      await axios.post(`${API}/social/whatsapp/contacts`, {
        contacts: [{ phone: newPhone.trim(), name: newName.trim() || newPhone.trim() }]
      }, { withCredentials: true });
      toast.success('Contact added');
      setNewName('');
      setNewPhone('');
      loadAll();
    } catch (e) {
      toast.error('Failed to add contact');
    } finally {
      setAddingContact(false);
    }
  };

  const deleteContact = async (phone) => {
    try {
      await axios.delete(`${API}/social/whatsapp/contacts/${phone}`, { withCredentials: true });
      toast.success('Contact removed');
      setContacts(prev => prev.filter(c => c.phone !== phone));
    } catch (e) {
      toast.error('Failed to delete contact');
    }
  };

  const toggleContact = (phone) => {
    setSelectedContacts(prev => ({ ...prev, [phone]: !prev[phone] }));
  };

  const selectAll = () => {
    const all = {};
    contacts.forEach(c => { all[c.phone] = true; });
    setSelectedContacts(all);
  };

  const deselectAll = () => setSelectedContacts({});

  const selectedCount = Object.values(selectedContacts).filter(Boolean).length;

  const sendBulk = async () => {
    if (!selectedTemplate) { toast.error('Select a template'); return; }
    if (selectedCount === 0) { toast.error('Select at least one contact'); return; }

    setSending(true);
    try {
      const recipients = contacts
        .filter(c => selectedContacts[c.phone])
        .map(c => ({ phone: c.phone, params: [] }));

      const { data } = await axios.post(`${API}/social/whatsapp/send-bulk`, {
        template_name: selectedTemplate,
        language_code: 'en',
        recipients,
      }, { withCredentials: true });

      toast.success(`Sent: ${data.sent}/${data.total} messages`);
      setSelectedContacts({});
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        <span className="ml-2 text-slate-500">Loading WhatsApp...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 p-4 md:p-6" data-testid="whatsapp-bulk-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-emerald-600" />
            WhatsApp Bulk Messaging
          </h1>
          <p className="text-sm text-slate-500 mt-1">Send template messages to multiple contacts</p>
        </div>
        {status?.connected ? (
          <Badge className="bg-emerald-100 text-emerald-700">
            <CheckCircle className="h-3 w-3 mr-1" /> Connected
          </Badge>
        ) : (
          <Badge className="bg-red-100 text-red-700">
            <AlertCircle className="h-3 w-3 mr-1" /> Not Configured
          </Badge>
        )}
      </div>

      {!status?.connected && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <p className="text-sm text-amber-800">WhatsApp Business API credentials are not configured. Contact admin to set up the integration.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contacts */}
        <Card className="border-slate-200" data-testid="contacts-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" /> Contacts ({contacts.length})
              </CardTitle>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={selectAll} className="text-xs">Select All</Button>
                <Button size="sm" variant="ghost" onClick={deselectAll} className="text-xs">Clear</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Add Contact */}
            <div className="flex gap-2">
              <Input
                placeholder="Name"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                className="flex-1"
                data-testid="contact-name-input"
              />
              <Input
                placeholder="Phone (e.g. 919876543210)"
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                className="flex-1"
                data-testid="contact-phone-input"
              />
              <Button size="sm" onClick={addContact} disabled={addingContact} data-testid="add-contact-btn">
                {addingContact ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              </Button>
            </div>

            {/* Contact List */}
            <div className="max-h-72 overflow-y-auto space-y-1">
              {contacts.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No contacts yet. Add contacts above.</p>
              ) : (
                contacts.map(c => (
                  <div
                    key={c.phone}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedContacts[c.phone] ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => toggleContact(c.phone)}
                    data-testid={`contact-${c.phone}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!selectedContacts[c.phone]}
                        onChange={() => toggleContact(c.phone)}
                        className="rounded"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{c.name}</p>
                        <p className="text-xs text-slate-400"><Phone className="h-2.5 w-2.5 inline mr-1" />{c.phone}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-red-500"
                      onClick={(e) => { e.stopPropagation(); deleteContact(c.phone); }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Send Panel */}
        <Card className="border-slate-200" data-testid="send-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-600" /> Send Message
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Template</Label>
              <select
                className="w-full mt-1 rounded-md border border-slate-200 p-2 text-sm"
                value={selectedTemplate}
                onChange={e => setSelectedTemplate(e.target.value)}
                data-testid="template-select"
              >
                <option value="">Select a template...</option>
                {templates.map(t => (
                  <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
                ))}
              </select>
              {templates.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No approved templates found. Create templates in the WhatsApp Business Manager.</p>
              )}
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-medium">{selectedCount}</span> contacts selected
              </p>
            </div>

            <Button
              className="w-full"
              onClick={sendBulk}
              disabled={sending || !selectedTemplate || selectedCount === 0 || !status?.connected}
              data-testid="send-bulk-btn"
            >
              {sending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send to {selectedCount} Contact{selectedCount !== 1 ? 's' : ''}</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Past Jobs */}
      {jobs.length > 0 && (
        <Card className="border-slate-200" data-testid="jobs-history">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-500" /> Message History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {jobs.map(job => (
                <div key={job.job_id} className="border rounded-lg">
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpandedJob(expandedJob === job.job_id ? null : job.job_id)}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs">{job.template_name}</Badge>
                      <span className="text-sm text-slate-600">
                        {job.sent}/{job.total} sent
                      </span>
                      {job.failed > 0 && (
                        <Badge className="bg-red-100 text-red-600 text-[10px]">{job.failed} failed</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">{job.created_at?.split('T')[0]}</span>
                      {expandedJob === job.job_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>
                  {expandedJob === job.job_id && (
                    <div className="border-t px-3 pb-3 pt-2 space-y-1">
                      {(job.results || []).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1">
                          <span className="text-slate-600">{r.phone}</span>
                          {r.status === 'sent' ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Sent</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-600 text-[10px]" title={r.error}>Failed</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
